// =============================================================
// PERPETUUM — RBAC permission model (single source of truth).
//
// Formalizes the Phase-3 People & access permission matrix
// (scripts/perpetuum-desktop-sections6.jsx: PERM_MODULES / PERM_DEFAULTS):
//   - Per-module ACCESS LEVEL (None / View / Edit / Full) → R / W / D.
//   - Card-tab (menu) visibility per module.
//   - Named groups (Line technician … Super-admin) with default levels.
//
// Binding constraint (PROJECT.md / REQ-people-access):
//   "granular permission matrix (R/W/D per module + card-tab visibility)".
//
// UI-less + framework-less: the server enforces these on /api/v1 routes; the
// apps render the same levels as cards. Single source of truth = here.
// =============================================================

/** Modules the permission matrix gates (mirrors prototype PERM_MODULES ids). */
export type PermModule =
  | "dev" // Devices & locations
  | "tasks" // Tasks & work orders
  | "spares" // Spares & inventory
  | "downtime" // Downtimes
  | "reports" // Reports
  | "admin"; // Settings & admin

export const PERM_MODULES: readonly PermModule[] = [
  "dev",
  "tasks",
  "spares",
  "downtime",
  "reports",
  "admin",
] as const;

/**
 * Access level per module. Numeric so it is ordered + storable as data.
 *   0 None  → no access
 *   1 View  → READ
 *   2 Edit  → READ + WRITE
 *   3 Full  → READ + WRITE + DELETE
 *
 * RBAC FIX (Wave 8): the `admin` module uses the SAME 4-level ladder as every
 * other module. The prototype rendered admin as None/View/Full (3 states), and
 * the old seed capped Super-admin at admin:2 — but `can` gates `delete` at
 * Full(3), so `can("admin","delete")` was UNREACHABLE, leaving every DELETE on
 * an admin-module resource (companies/departments/user-groups/users) dead.
 * Admin-capable groups now seed admin:Full(3) so delete is reachable; level 2
 * means Edit (read+write, no delete) consistently with the other modules.
 */
export type AccessLevel = 0 | 1 | 2 | 3;

export const ACCESS = {
  NONE: 0,
  VIEW: 1,
  EDIT: 2,
  FULL: 3,
} as const;

/** A CRUD-ish operation gated against an access level. */
export type Operation = "read" | "write" | "delete";

/** The level required to perform an operation. */
const REQUIRED_LEVEL: Record<Operation, AccessLevel> = {
  read: ACCESS.VIEW,
  write: ACCESS.EDIT,
  delete: ACCESS.FULL,
};

/**
 * A user group's permission set: an access level per module plus the set of
 * module card-tabs the group may see. Stored as DATA (per-tenant + per-role).
 */
export interface GroupPermissions {
  /** Access level per module (missing module ⇒ None). */
  levels: Partial<Record<PermModule, AccessLevel>>;
  /** Card-tabs (module surfaces) visible to the group. Empty ⇒ derive from levels. */
  visibleCardTabs?: PermModule[];
}

/** The 5 seed groups from the Phase-3 matrix. */
export type SeedGroup =
  | "Line technician"
  | "Maintenance planner"
  | "Warehouse keeper"
  | "Chief engineer"
  | "Super-admin";

/**
 * Default access levels per group per module — copied verbatim from the
 * prototype PERM_DEFAULTS so the server enforces exactly the verified matrix.
 */
export const PERM_DEFAULTS: Record<SeedGroup, Record<PermModule, AccessLevel>> = {
  "Line technician": { dev: 1, tasks: 2, spares: 1, downtime: 2, reports: 1, admin: 0 },
  "Maintenance planner": { dev: 2, tasks: 3, spares: 2, downtime: 2, reports: 1, admin: 0 },
  "Warehouse keeper": { dev: 1, tasks: 1, spares: 3, downtime: 1, reports: 1, admin: 0 },
  // Chief engineer administers at VIEW only (read the People&Access / security
  // screens) — it is NOT an admin-capable group, so it stays at admin:1 and
  // cannot delete admin-module resources.
  "Chief engineer": { dev: 3, tasks: 3, spares: 3, downtime: 3, reports: 2, admin: 1 },
  // Super-admin is the admin-capable group: admin Full(3) so READ + WRITE +
  // DELETE are all reachable — `can("admin","delete")` is now TRUE (Wave-8 fix).
  "Super-admin": { dev: 3, tasks: 3, spares: 3, downtime: 3, reports: 2, admin: 3 },
};

/** Build a GroupPermissions from a level map (card-tabs = any module with View+). */
export function groupFromLevels(
  levels: Partial<Record<PermModule, AccessLevel>>,
): GroupPermissions {
  const visibleCardTabs = PERM_MODULES.filter(
    (m) => (levels[m] ?? ACCESS.NONE) >= ACCESS.VIEW,
  );
  return { levels, visibleCardTabs };
}

/** The effective level for one module under a group (None if unset). */
export function levelFor(perms: GroupPermissions, mod: PermModule): AccessLevel {
  return perms.levels[mod] ?? ACCESS.NONE;
}

/**
 * Core RBAC predicate: may this group perform `op` on `module`?
 * read needs View(1), write needs Edit(2), delete needs Full(3).
 */
export function can(
  perms: GroupPermissions,
  mod: PermModule,
  op: Operation,
): boolean {
  return levelFor(perms, mod) >= REQUIRED_LEVEL[op];
}

/** Is the module's card-tab visible to the group? (visibility ⊇ View access). */
export function cardTabVisible(perms: GroupPermissions, mod: PermModule): boolean {
  if (perms.visibleCardTabs && perms.visibleCardTabs.length > 0) {
    return perms.visibleCardTabs.includes(mod);
  }
  // Fallback: any View-or-better level implies the tab is visible.
  return levelFor(perms, mod) >= ACCESS.VIEW;
}

/**
 * Merge several group permission sets into one effective set (a user may be in
 * many groups → most-permissive level wins; card-tabs union).
 */
export function mergePermissions(groups: GroupPermissions[]): GroupPermissions {
  const levels: Partial<Record<PermModule, AccessLevel>> = {};
  const tabs = new Set<PermModule>();
  for (const g of groups) {
    for (const m of PERM_MODULES) {
      const lvl = g.levels[m] ?? ACCESS.NONE;
      if (lvl > (levels[m] ?? ACCESS.NONE)) levels[m] = lvl;
    }
    for (const t of g.visibleCardTabs ?? []) tabs.add(t);
  }
  // If no explicit tabs supplied, derive from merged levels.
  const visibleCardTabs =
    tabs.size > 0
      ? [...tabs]
      : PERM_MODULES.filter((m) => (levels[m] ?? ACCESS.NONE) >= ACCESS.VIEW);
  return { levels, visibleCardTabs };
}

/**
 * Human-readable level name (for admin UIs / audit). EVERY module — including
 * `admin` — now uses the same 4-level ladder (None/View/Edit/Full), so the name
 * matches what `can` actually enforces. (Pre-Wave-8 the admin module collapsed
 * 2→"Full"; that masked the unreachable delete gate. The `mod` param is kept
 * for call-site compatibility and future per-module naming.)
 */
export function levelName(lvl: AccessLevel, _mod?: PermModule): string {
  return ["None", "View", "Edit", "Full"][lvl] ?? "None";
}

/** Map a string permission token ("tasks.write") → module + operation. */
export function parsePermissionToken(
  token: string,
): { module: PermModule; op: Operation } | null {
  const [mod, op] = token.split(".");
  if (!mod || !op) return null;
  if (!(PERM_MODULES as readonly string[]).includes(mod)) return null;
  if (op !== "read" && op !== "write" && op !== "delete") return null;
  return { module: mod as PermModule, op };
}
